import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const THEMES = {
  midnight: {
    id: 'midnight',
    name: 'Midnight Fleet',
    class: 'theme-midnight',
    colors: {
      bg: '#0A0E1A',
      card: '#141B2D',
      primary: '#3B82F6',
      accent: '#F97316',
      success: '#10B981'
    },
    backgrounds: [
      { id: 'solid', name: 'Solid', style: 'bg-background' },
      { id: 'gradient', name: 'Gradient', style: 'bg-gradient-to-br from-[#0A0E1A] via-[#1E3A5F] to-[#0A0E1A]' },
      { id: 'mesh', name: 'Mesh', style: 'bg-[radial-gradient(ellipse_at_top,_#1E3A5F_0%,_#0A0E1A_50%,_#0A0E1A_100%)]' }
    ]
  },
  cyber: {
    id: 'cyber',
    name: 'Cyber Logistics',
    class: 'theme-cyber',
    colors: {
      bg: '#0D0B14',
      card: '#1A1625',
      primary: '#7C3AED',
      accent: '#FF6B35',
      success: '#84CC16'
    },
    backgrounds: [
      { id: 'solid', name: 'Solid', style: 'bg-background' },
      { id: 'gradient', name: 'Gradient', style: 'bg-gradient-to-br from-[#0D0B14] via-[#2D1B4E] to-[#0D0B14]' },
      { id: 'mesh', name: 'Mesh', style: 'bg-[radial-gradient(ellipse_at_top,_#2D1B4E_0%,_#0D0B14_50%,_#0D0B14_100%)]' }
    ]
  },
  neon: {
    id: 'neon',
    name: 'Neon Dispatch',
    class: 'theme-neon',
    colors: {
      bg: '#09090B',
      card: '#18181B',
      primary: '#06B6D4',
      accent: '#EA580C',
      success: '#22C55E'
    },
    backgrounds: [
      { id: 'solid', name: 'Solid', style: 'bg-background' },
      { id: 'gradient', name: 'Gradient', style: 'bg-gradient-to-br from-[#09090B] via-[#0C4A4A] to-[#09090B]' },
      { id: 'mesh', name: 'Mesh', style: 'bg-[radial-gradient(ellipse_at_top,_#0C4A4A_0%,_#09090B_50%,_#09090B_100%)]' }
    ]
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('leader-hub-theme');
    return saved && THEMES[saved] ? saved : 'midnight';
  });

  const [background, setBackground] = useState(() => {
    const saved = localStorage.getItem('leader-hub-background');
    return saved || 'solid';
  });

  useEffect(() => {
    const root = document.documentElement;
    // Remove all theme classes
    Object.values(THEMES).forEach(t => root.classList.remove(t.class));
    // Add current theme class
    root.classList.add(THEMES[theme].class);
    localStorage.setItem('leader-hub-theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('leader-hub-background', background);
  }, [background]);

  const currentTheme = THEMES[theme];
  const currentBackground = currentTheme.backgrounds.find(b => b.id === background) || currentTheme.backgrounds[0];

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      currentTheme, 
      themes: THEMES,
      background,
      setBackground,
      currentBackground
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
